import { Options, ViewModel } from './wasmBridge/viewModel';
import { BOARD_PALETTES, type BoardPalette } from './board-palettes.ts';

export default abstract class Puzzle {
  abstract get name(): string;
  abstract get description(): string;
  abstract get goal(): string;
  abstract get instructions(): string[];

  abstract get difficultyLevels(): Map<string, Options[]>;
  abstract hint(viewModel: ViewModel, preset: string): void;

  applyTheme(palette: BoardPalette) {
    this._palette = palette;
    this._colourOverrides = new Map([
      ['#e6e6e6', palette.board.background],
      ['#d4d4d4', palette.board.background],
      ['#000000', palette.board.grid.primary],
      ['#737373', palette.board.grid.secondary],
      ['#ff0000', palette.board.grid.highlighted],
      ['#0000ff', palette.board.darkFill],
      ['#aaaaaa', palette.board.lightFill],
    ]);
  }

  protected _palette: BoardPalette = BOARD_PALETTES.blue;
  protected _colourOverrides: Map<string, string> = new Map();
  get colourOverrides() { return this._colourOverrides; }

  get hintsSupported() {
    return true;
  }

  get solutionSupported() {
    return true;
  }

  // True when this puzzle ships suggested "default boards" (the curated
  // Basic / Medium / Hard / Really Hard! vocabulary) that replace the
  // WASM engine presets in the board picker. Puzzles whose difficultyLevels
  // merely mirror the engine presets (pegs) keep the engine view.
  get hasDefaultBoards() {
    return false;
  }

  // True when this puzzle has a configurable difficulty option separate
  // from its board dimensions / region count (Unruly, Maps, Tents). Only
  // these puzzles show the difficulty level name in the UI; for the others
  // (Rectangles, Untangle) difficulty is conveyed by the board itself.
  get hasDifficultyOption() {
    return false;
  }

  // Default-board labels carry the difficulty level name (e.g. "6 x 6 Basic").
  // Puzzles whose difficulty is conveyed by the board size/point count alone
  // (rectangles, untangle, pegs) drop that suffix when displaying.
  displayLabel(label: string) {
    return this.hasDifficultyOption
      ? label
      : label.replace(/\s+(Basic|Medium|Hard|Really Hard!)$/, '');
  }

  // ToDo: pass a single state/move rather than accumulating all of them
  // ToDo: implement for puzzles other than Unruly
  protected _initialState: Set<string> = new Set<string>();
  onInitialise(state: string) {
    this._initialState.add(state);
  }

  // ToDo: implement for puzzles other than Unruly
  protected _moves: string[] = [];
  onMove(move: string) {
    this._moves.push(move);
  }
}

export async function getPuzzles() {
  const puzzleModules = import.meta.glob('./models/*.ts');

  const puzzles = [] as Puzzle[];
  for (const path in puzzleModules) {
    const ctor = await puzzleModules[path]() as any;
    puzzles.push(new ctor.default() as Puzzle);
  }

  return puzzles;
}


export async function getPuzzle(name: string) {
  const puzzleModules = import.meta.glob('./models/*.ts');

  const puzzlesMap = {} as any;
  for (const path in puzzleModules) {
    const puzzleName = path.match(/\/([^\/]+)\.ts$/)[1];
    puzzlesMap[puzzleName] = puzzleModules[path];
  }

  const ctor = await puzzlesMap[name]();
  return new ctor.default() as Puzzle;
}
